import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal } from "lucide-react";
import { colors } from "@/utils/colors";
import { formatCurrency } from "@/utils/currency";

interface DistributionProcess {
  id: number;
  uuid: string;
  cp_classification_id: number;
  from_unity_id?: number;
  unity_id: number;
  quota_quantity: number;
  from_seller_id?: number;
  seller_id?: number;
  distribution_type_id: number;
  distribution_process_status_id: number;
  created_at: string;
  updated_at: string;
  processed_at?: string;
  created_by: {
    id: number;
    name: string;
  };
  updated_by?: {
    id: number;
    name: string;
  };
  seller?: {
    id: number;
    uuid: string;
    user: {
      id: number;
      name: string;
    };
  };
  from_seller?: {
    id: number;
    uuid: string;
    user: {
      id: number;
      name: string;
    };
  };
  unity: {
    id: number;
    name: string;
  };
  from_unity?: {
    id: number;
    name: string;
  };
  contest_product_classification: {
    id: number;
    product_name: string;
    contest: {
      id: number;
      name: string;
    };
    lottery: {
      id: number;
      name: string;
      color: string;
    };
    product_classification: {
      id: number;
      name: string;
      quota_quantity: number;
    };
  };
  distribution_type: {
    id: number;
    name: string;
  };
  distribution_process_status: {
    id: number;
    name: string;
  };
}

interface PaginationData {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  data: DistributionProcess[];
}

interface TableDataProps {
  dataApi: PaginationData;
  pageActions: {
    previous: () => void;
    next: () => void;
    lastPage: () => void;
  };
}

export const TableData = ({ dataApi, pageActions }: TableDataProps) => {
  // Verifica se é aceito automaticamente (Admin -> LOTERIAS ALDEOTA com qty 1)
  const isAutoAccepted = (process: DistributionProcess) => {
    const isFromAdmin = !process.from_unity && !process.from_seller;
    const isToLoteriasAldeota = process.unity?.name?.toUpperCase().includes('LOTERIAS ALDEOTA');
    const isQtyOne = process.quota_quantity === 1;
    const isAccepted = process.distribution_process_status?.name === "Aceito";
    const isAdminToUnity = process.distribution_type_id === 1;

    return isFromAdmin && isToLoteriasAldeota && isQtyOne && isAccepted && isAdminToUnity;
  };

  const getProcessStatusBadge = (statusName: string, distributionTypeId?: number, process?: DistributionProcess) => {
    // Aceito automaticamente (Admin -> LOTERIAS ALDEOTA com qty 1)
    if (process && isAutoAccepted(process)) {
      return (
        <Badge className="bg-emerald-500 text-white font-semibold whitespace-nowrap">
          ACEITO AUTO
        </Badge>
      );
    }

    // Pré-venda aceita mostra "Aceito Automaticamente" em duas linhas
    const isPreSaleAccepted = distributionTypeId === 7 && statusName === "Aceito";

    if (isPreSaleAccepted) {
      return (
        <Badge className="bg-cyan-500 text-white text-center leading-tight py-1">
          Aceito<br/>Automaticamente
        </Badge>
      );
    }

    const colorMap: Record<string, string> = {
      Pendente: "bg-yellow-500",
      Aceito: "bg-green-500",
      Rejeitado: "bg-red-500",
    };
    return (
      <Badge
        className={cn("text-white", colorMap[statusName] || "bg-gray-500")}
      >
        {statusName}
      </Badge>
    );
  };

  const getSenderInfo = (process: DistributionProcess) => {
    if (process.from_unity) {
      return `U: ${process.from_unity.name}`;
    }
    if (process.from_seller?.user?.name) {
      return `V: ${process.from_seller.user.name}`;
    }

    if (process?.created_by?.name) {
      return ` ${process?.created_by?.name}`;
    }
    return "-";
  };

  const getReceiverInfo = (process: DistributionProcess) => {
    if (process.unity && !process.seller) {
      return `U: ${process.unity.name}`;
    }
    if (process.seller?.user?.name) {
      return `V: ${process.seller.user.name}`;
    }
    return "-";
  };

  const getValueFromClassification = (process: DistributionProcess) => {
    const priceMatch =
      process.contest_product_classification.product_classification.name.match(
        /R\$\s*([\d,\.]+)/
      );
    if (priceMatch) {
      const price = parseFloat(priceMatch[1].replace(",", "."));
      return price * process.quota_quantity;
    }
    return 0;
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead className="font-semibold">ID</TableHead>
            <TableHead className="font-semibold">Data</TableHead>
            <TableHead className="font-semibold">
              Tipo de Distribuição
            </TableHead>
            <TableHead className="font-semibold">Concurso</TableHead>
            <TableHead className="font-semibold">Produto</TableHead>
            <TableHead className="font-semibold">Remetente</TableHead>
            <TableHead className="font-semibold">Destinatário</TableHead>
            <TableHead className="font-semibold text-right">
              Quantidade
            </TableHead>
            <TableHead className="font-semibold text-right">Valor</TableHead>
            <TableHead className="font-semibold">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((process) => {
            const isPreSale = process.distribution_type_id === 7;
            const isAutoAcceptedRow = isAutoAccepted(process);
            return (
            <TableRow
              key={process.uuid}
              className={cn(
                "hover:bg-gray-50",
                isPreSale && "bg-yellow-50 border-l-4 border-l-yellow-500 hover:bg-yellow-100",
                isAutoAcceptedRow && "bg-yellow-50 hover:bg-yellow-100"
              )}
            >
              <TableCell className="font-medium">{process.id}</TableCell>
              <TableCell>
                {format(new Date(process.created_at), "dd/MM/yyyy HH:mm", {
                  locale: ptBR,
                })}
              </TableCell>
              <TableCell>
                {isPreSale ? (
                  <Badge className="bg-yellow-400 text-yellow-900 font-semibold hover:bg-yellow-500">
                    {process.distribution_type.name}
                  </Badge>
                ) : (
                  <span className="text-sm">
                    {process.distribution_type.name}
                  </span>
                )}
              </TableCell>
              <TableCell>
                <div>
                  <p className="font-medium">
                    {process?.contest_product_classification?.contest?.name}
                  </p>
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block w-3 h-3 rounded-sm border"
                      style={{
                        backgroundColor: process.contest_product_classification
                          .lottery?.color
                          ? process.contest_product_classification.lottery.color
                          : "#6B7280",
                        borderColor: "#d1d5db",
                      }}
                    />
                    <span className="text-xs">
                      {process.contest_product_classification.lottery.name}
                    </span>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <p className="text-sm">
                  {process.contest_product_classification.product_name}
                </p>
                <p className="text-xs text-gray-500">
                  {
                    process.contest_product_classification
                      ?.product_classification?.name
                  }
                </p>
              </TableCell>
              <TableCell>
                <p className="text-sm">{getSenderInfo(process)}</p>
              </TableCell>
              <TableCell>
                <p className="text-sm">{getReceiverInfo(process)}</p>
              </TableCell>
              <TableCell className="text-right font-medium">
                {process.quota_quantity}
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatCurrency(getValueFromClassification(process))}
              </TableCell>
              <TableCell>
                {getProcessStatusBadge(
                  process.distribution_process_status.name,
                  process.distribution_type_id,
                  process
                )}
              </TableCell>
            </TableRow>
          );
          })}
          {(!dataApi?.data || dataApi.data.length < 1) && (
            <TableRow>
              <TableCell colSpan={10} className="text-center py-8">
                <p className="text-gray-500">Nenhuma distribuição encontrada</p>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
